/**
 * The baked airport database format — shared by the baker and the runtime reader.
 *
 * ## Why binary rather than JSON
 *
 * OurAirports is about 14,000 land airports with 20,000 runways once the filtering in
 * `tools/bake-airports.ts` is done. As JSON that is roughly 5 MB, which costs a 5 MB download,
 * a ~50 ms `JSON.parse`, and — the part that actually hurts — 14,000 objects and 34,000 strings
 * on the heap forever, most of which will never be looked at. As a flat binary it is about
 * 1.6 MB, it is *read* rather than parsed, and the runtime can keep it as one `ArrayBuffer` and
 * materialise an object only for the handful of airports anyone actually asks about.
 *
 * The layout is deliberately dull: a small header, one UTF-8 text blob, then two arrays of
 * fixed-size records that index into it. Fixed strides mean the nearest-airport search is a
 * `for` loop over a `DataView` with no pointer chasing and no allocation, which is what lets it
 * run without a spatial index at all.
 *
 * ## Coordinates
 *
 * Latitude and longitude are `int32` scaled by 10⁷. That is not a space optimisation — it is a
 * precision decision. `float32` degrees resolve to about 1 m at the equator, which is enough to
 * put a runway threshold visibly off its centreline and enough to make a 3,000 m runway's
 * computed heading wrong by a tenth of a degree. Scaled integers give 1.1 cm and are exact.
 */

/** File magic: "SKYA". */
export const AIRPORT_DB_MAGIC = 0x534b5941;
/** Bump when the layout changes, so a stale baked file is rejected rather than misread. */
export const AIRPORT_DB_VERSION = 1;

/** Bytes per airport record. */
export const AIRPORT_RECORD_BYTES = 28;
/** Bytes per runway record. */
export const RUNWAY_RECORD_BYTES = 36;

/** Degrees are stored as `round(degrees × 1e7)`. */
export const COORD_SCALE = 1e7;
/** True headings are stored as `round(degrees × 10)`. */
export const HEADING_SCALE = 10;

/**
 * Header layout, in bytes from the start of the file.
 * `magic, version, airportCount, runwayCount, textBytes` — all `uint32`.
 */
export const HEADER_BYTES = 20;

/** Runway surfaces, collapsed to what the renderer actually needs to know. */
export const SURFACES = ['asphalt', 'concrete', 'grass', 'gravel', 'dirt', 'water', 'unknown'] as const;
export type RunwaySurface = (typeof SURFACES)[number];

/** Bit flags on a runway record. */
export const RUNWAY_LIGHTED = 1 << 0;
export const RUNWAY_CLOSED = 1 << 1;

/**
 * Airport record field offsets, in bytes from the start of the record.
 *
 * Written out rather than computed so the baker and the reader cannot drift: both index this
 * one object, and a mismatch is a compile error rather than airports appearing in the sea.
 */
export const AIRPORT = {
  latitudeE7: 0, // int32
  longitudeE7: 4, // int32
  elevationM: 8, // int16
  runwayCount: 10, // uint16
  runwayIndex: 12, // uint32 — index into the runway array
  icaoOffset: 16, // uint32 — byte offset into the text blob
  icaoLength: 20, // uint8
  reserved: 21, // uint8
  nameLength: 22, // uint16
  nameOffset: 24, // uint32
} as const;

/** Runway record field offsets, in bytes from the start of the record. */
export const RUNWAY = {
  lowLatitudeE7: 0, // int32 — the low-numbered end
  lowLongitudeE7: 4, // int32
  highLatitudeE7: 8, // int32 — the reciprocal end
  highLongitudeE7: 12, // int32
  lengthM: 16, // uint16
  widthM: 18, // uint16
  headingDeciDeg: 20, // uint16 — true heading of the low end × 10
  surface: 22, // uint8 — index into SURFACES
  flags: 23, // uint8
  identOffset: 24, // uint32 — low ident, immediately followed by the high ident
  lowIdentLength: 28, // uint8
  highIdentLength: 29, // uint8
  lowElevationM: 30, // int16 — threshold elevation of the low end
  highElevationM: 32, // int16 — threshold elevation of the high end; the two give the slope
  placement: 34, // uint8 — how the geometry was derived; see PLACEMENT
  reserved: 35, // uint8
} as const;

/**
 * How a runway's geometry was derived, in descending order of trustworthiness.
 *
 * Kept in the data because it changes what the sim may reasonably do with a runway. A
 * `surveyed` runway is good enough to fly an approach to; an `inferred` one is placed on the
 * airport reference point along a published heading, which is right to within a few hundred
 * metres and is the difference between an airport existing and not existing at all.
 */
export const PLACEMENT = {
  /** Both thresholds surveyed. Heading computed from them. */
  surveyed: 0,
  /** One threshold surveyed; the other projected along the published heading. */
  projected: 1,
  /** Neither threshold surveyed; centred on the airport reference point. */
  inferred: 2,
} as const;

export type PlacementQuality = (typeof PLACEMENT)[keyof typeof PLACEMENT];

/** Where the baked file is served from. Lazy-loaded, never bundled. */
export const AIRPORT_DB_URL = 'data/airports.bin';
